import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/contexts/SettingsContext";
import { Section, Overline, SectionTitle, GoldDivider } from "@/components/Primitives";
import Amenities from "@/components/Amenities";
import Payments from "@/components/Payments";
import { ArrowRight, MapPin, Phone, MessageCircle } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1547595628-c61a29f496f0?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function LocalSeo() {
  const { t } = useTranslation();
  const { settings: s } = useSettings();
  const items = t("local_seo.why_items", { returnObjects: true }) || [];
  const keywords = t("local_seo.keywords", { returnObjects: true }) || [];

  return (
    <div data-testid="local-seo-page" className="pt-32">
      {/* Hero */}
      <section className="relative" data-testid="local-seo-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Overline>{t("local_seo.overline")}</Overline>
            <h1 className="font-serif-luxe text-5xl sm:text-6xl font-light tracking-tight text-[#F8F5EF] mt-4 mb-6">{t("local_seo.title")}</h1>
            <p className="text-lg text-[#F8F5EF]/80 leading-relaxed max-w-2xl">{t("local_seo.intro")}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/reservation" className="btn-gold" data-testid="seo-reserve-btn">{t("local_seo.cta")} <ArrowRight size={14} /></Link>
              <Link to="/menu" className="btn-ghost-gold">{t("hero.cta_menu")}</Link>
            </div>
            {s && (
              <div className="mt-10 flex flex-wrap gap-6 text-sm">
                <a href={`tel:${(s.phone || "").replace(/\s/g, "")}`} className="flex items-center gap-2 text-[#F8F5EF]/85 hover:text-[#D4AF37]"><Phone size={14} className="text-[#D4AF37]" /> {s.phone}</a>
                <a href={`https://wa.me/${s.whatsapp_raw}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#F8F5EF]/85 hover:text-[#D4AF37]"><MessageCircle size={14} className="text-[#D4AF37]" /> {s.whatsapp}</a>
                <a href={s.maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#F8F5EF]/85 hover:text-[#D4AF37]"><MapPin size={14} className="text-[#D4AF37]" /> {s.address}</a>
              </div>
            )}
          </div>
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden">
              <img src={HERO_IMG} alt="Restaurant Le 17 Mulhouse" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 border border-[#D4AF37]/20" />
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <Section testId="local-seo-why" className="bg-[#0A0A0A]">
        <Overline>—</Overline>
        <SectionTitle>{t("local_seo.why_title")}</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {items.map((it, idx) => (
            <div key={idx} className="luxe-card p-8" data-testid={`seo-why-${idx}`}>
              <div className="text-[#D4AF37] font-serif-luxe text-3xl mb-3">{String(idx + 1).padStart(2, "0")}</div>
              <h3 className="font-serif-luxe text-2xl text-[#F8F5EF] mb-3">{it.title}</h3>
              <p className="text-sm text-[#F8F5EF]/70 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Amenities */}
      <Section testId="local-seo-amenities">
        <Amenities />
        <div className="mt-12">
          <Payments />
        </div>
      </Section>

      {/* Nearby */}
      <Section testId="local-seo-nearby" className="bg-[#0A0A0A]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <Overline>—</Overline>
            <SectionTitle>{t("local_seo.nearby_title")}</SectionTitle>
            <p className="text-[#F8F5EF]/75 leading-relaxed">{t("local_seo.nearby_text")}</p>
          </div>
          <div className="lg:col-span-7">
            {s?.maps_embed && (
              <iframe title="Le 17 Mulhouse" src={s.maps_embed} className="w-full h-[420px] border border-[#D4AF37]/20 grayscale contrast-125" loading="lazy" />
            )}
          </div>
        </div>
      </Section>

      {/* Keywords cloud */}
      <Section testId="local-seo-keywords">
        <Overline>—</Overline>
        <SectionTitle>{t("local_seo.keywords_title")}</SectionTitle>
        <div className="flex flex-wrap gap-2 mt-6">
          {keywords.map((kw, i) => (
            <span key={i} className="px-4 py-2 border border-[#D4AF37]/25 text-[#F8F5EF]/80 text-xs tracking-[0.1em] hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors">{kw}</span>
          ))}
        </div>
        <GoldDivider />
        <div className="text-center">
          <Link to="/reservation" className="btn-gold" data-testid="seo-final-cta">{t("local_seo.cta")} <ArrowRight size={14} /></Link>
        </div>
      </Section>
    </div>
  );
}
